import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, RefreshCw, ArrowRightLeft, Loader2, Image as ImageIcon, ChevronLeft, ChevronRight, ExternalLink, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/semantic-audit`;

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'bg-slate-200 text-slate-700' },
  correct: { label: '✅ Correct', color: 'bg-emerald-100 text-emerald-800' },
  wrong_regen: { label: '🔄 Regen queued', color: 'bg-amber-100 text-amber-800' },
  wrong_reassign: { label: '📋 Wrong (reassign)', color: 'bg-rose-100 text-rose-800' },
};

export const SemanticAuditReviewTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_flagged: 0, pending: 0, review_counters: {} });
  const [queue, setQueue] = useState({ counters: {}, recent: [] });
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [regenTopic, setRegenTopic] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [submitting, setSubmitting] = useState(false);

  const token = () => localStorage.getItem('session_token');
  const H = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [flagRes, statsRes, queueRes] = await Promise.all([
        axios.get(`${API}/flagged?status=${statusFilter}&limit=1000`, H()),
        axios.get(`${API}/stats`, H()),
        axios.get(`${API}/regen-queue`, H()),
      ]);
      setItems(flagRes.data.items || []);
      setStats(statsRes.data);
      setQueue(queueRes.data);
      setIdx(0);
    } catch (e) {
      toast.error('Failed to load audit data: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Poll the regen queue every 5s so operator sees progress
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/regen-queue`, H());
        setQueue(r.data);
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const current = items[idx];

  useEffect(() => {
    setNotes(current?.review_notes || '');
    setRegenTopic('');
  }, [idx, current?.question_id]);

  const submit = async (status) => {
    if (!current) return;
    setSubmitting(true);
    try {
      const body = { status, notes: notes || undefined };
      if (status === 'wrong_regen' && regenTopic) body.regen_topic = regenTopic;
      await axios.post(`${API}/review/${current.question_id}`, body, H());
      toast.success(
        status === 'correct' ? 'Marked correct' :
        status === 'wrong_regen' ? 'Queued for regeneration' :
        'Marked wrong (reassign later)'
      );
      // Optimistically advance
      const nextIdx = Math.min(idx + 1, items.length - 1);
      setIdx(nextIdx);
      // Refresh counters
      const [statsRes, queueRes] = await Promise.all([
        axios.get(`${API}/stats`, H()),
        axios.get(`${API}/regen-queue`, H()),
      ]);
      setStats(statsRes.data);
      setQueue(queueRes.data);
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const kickWorker = async () => {
    try {
      const r = await axios.post(`${API}/regen-queue/process`, {}, H());
      toast.info(r.data.status === 'already_running' ? 'Worker already running' : 'Regen worker started');
      const q = await axios.get(`${API}/regen-queue`, H());
      setQueue(q.data);
    } catch (e) {
      toast.error('Failed to start worker: ' + (e.response?.data?.detail || e.message));
    }
  };

  const batchEnqueueAll = async () => {
    // Dry-run first so operator can see the count and confirm
    try {
      const dry = await axios.post(`${API}/regen-queue/batch-enqueue`, { dry_run: true }, H());
      const n = dry.data.would_queue || 0;
      if (n === 0) {
        toast.info('Nothing to enqueue — all pending items are already reviewed or queued.');
        return;
      }
      const ok = window.confirm(
        `Vision-system-guided regen will queue ${n} pending flagged items.\n\n` +
        `Each will get a new image generated using its stimulus + question text + Vision Verifier reason.\n\n` +
        `Est. runtime: ~${Math.ceil(n * 15 / 60)} minutes.\n` +
        `Est. cost: ~$${(n * 0.04).toFixed(2)} in MOSAIC image engine credits.\n\n` +
        `Every result is written to R2 and updates production immediately (shared bucket).\n` +
        `You can spot-check each in the Audit Review tab as they complete.\n\n` +
        `Proceed?`
      );
      if (!ok) return;
      const r = await axios.post(`${API}/regen-queue/batch-enqueue`, { dry_run: false }, H());
      toast.success(`Queued ${r.data.queued} jobs. Starting worker…`);
      // Auto-kick the worker
      await axios.post(`${API}/regen-queue/process`, {}, H());
      const q = await axios.get(`${API}/regen-queue`, H());
      setQueue(q.data);
      await loadAll();
    } catch (e) {
      toast.error('Batch enqueue failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const totalReviewed = useMemo(
    () => Object.values(stats.review_counters || {}).reduce((a, b) => a + b, 0),
    [stats]
  );
  const pctReviewed = stats.total_flagged
    ? Math.round((totalReviewed / stats.total_flagged) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="semantic-audit-review-tab">
      {/* Batch action banner */}
      <Card className="p-4 border-2 border-blue-300 bg-blue-50">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <div className="text-sm font-bold text-blue-900 flex items-center gap-2">
              <Zap size={16} /> Vision Verifier Guided Regen
            </div>
            <div className="text-xs text-blue-800 mt-1">
              One-click regenerate every pending flagged image using its stimulus +
              question text + Vision Verifier reason as combined prompt guidance. Each
              result is written to R2 and updates production immediately.
            </div>
          </div>
          <Button
            onClick={batchEnqueueAll}
            className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
            data-testid="audit-batch-enqueue-btn"
          >
            <Zap size={16} className="mr-2" /> Batch regen all pending
          </Button>
        </div>
      </Card>

      {/* Header + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total flagged</div>
          <div className="text-2xl font-bold text-slate-900">{stats.total_flagged}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Reviewed</div>
          <div className="text-2xl font-bold text-emerald-700">{totalReviewed}</div>
          <Progress value={pctReviewed} className="mt-2 h-2" />
          <div className="text-xs text-slate-500 mt-1">{pctReviewed}% done</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Regen queue</div>
          <div className="text-sm text-slate-700 mt-1 space-y-0.5">
            <div>queued: <b>{queue.counters.queued || 0}</b></div>
            <div>in progress: <b className="text-amber-600">{queue.counters.in_progress || 0}</b></div>
            <div>done: <b className="text-emerald-700">{queue.counters.done || 0}</b></div>
            <div>failed: <b className="text-rose-700">{queue.counters.failed || 0}</b></div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 w-full"
            onClick={kickWorker}
            data-testid="audit-kick-worker-btn"
          >
            <RefreshCw size={14} className="mr-1" /> Run regen worker
          </Button>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Filter</div>
          <select
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="audit-status-filter"
          >
            <option value="pending">Pending only ({stats.pending || 0})</option>
            <option value="correct">Marked correct</option>
            <option value="wrong_regen">Queued for regen</option>
            <option value="wrong_reassign">Marked wrong</option>
            <option value="">All</option>
          </select>
        </Card>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="animate-spin mx-auto text-slate-400" size={32} />
          <div className="text-slate-500 mt-2">Loading audit data…</div>
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
          <div className="text-lg font-semibold text-slate-800 mt-2">Nothing in this bucket</div>
          <div className="text-sm">Change the filter above to see other statuses.</div>
        </Card>
      ) : current ? (
        <Card className="p-6">
          {/* nav */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-600">
              Item <b>{idx + 1}</b> of <b>{items.length}</b>
              &nbsp;·&nbsp;Grade <b>{current.grade_level || '?'}</b>
              &nbsp;·&nbsp;{current.modality}
              &nbsp;·&nbsp;<span className={`px-2 py-0.5 rounded ${STATUS_LABELS[current.review_status]?.color}`}>
                {STATUS_LABELS[current.review_status]?.label}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={idx === 0}
                onClick={() => setIdx(idx - 1)}
                data-testid="audit-prev-btn"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={idx >= items.length - 1}
                onClick={() => setIdx(idx + 1)}
                data-testid="audit-next-btn"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>

          {/* main split: image left, content right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1">
                <ImageIcon size={12} /> Current scene image
              </div>
              {current.image_url ? (
                <>
                  <img
                    src={current.image_url}
                    alt="scene"
                    className="rounded-lg border w-full h-auto"
                    data-testid="audit-scene-img"
                  />
                  <a
                    href={current.image_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 underline mt-1 inline-flex items-center gap-1"
                  >
                    Open full-size <ExternalLink size={10} />
                  </a>
                </>
              ) : (
                <div className="p-6 border rounded bg-slate-50 text-sm text-slate-500">No image URL.</div>
              )}
              {current.new_image_url && (
                <div className="mt-4">
                  <div className="text-xs uppercase text-emerald-700 mb-1">Regenerated replacement</div>
                  <img src={current.new_image_url} alt="new" className="rounded-lg border w-full" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">Stimulus (what the audio narrates)</div>
                <div className="p-3 bg-slate-50 border rounded text-sm italic text-slate-800">
                  {current.stimulus || <span className="text-slate-400">(none)</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">Question</div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm font-medium text-slate-900">
                  {current.question_text || <span className="text-slate-400">(none)</span>}
                </div>
              </div>
              {current.answer_choices?.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Answer choices</div>
                  <ol className="text-sm text-slate-700 space-y-1 list-[upper-alpha] pl-6">
                    {current.answer_choices.map((c, i) => (
                      <li key={i} className={c === current.correct_answer ? 'font-bold text-emerald-700' : ''}>{c}</li>
                    ))}
                  </ol>
                </div>
              )}
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">Vision Verifier reason (why it flagged)</div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-slate-800">
                  {current.audit_reason || <span className="text-slate-400">(no reason recorded)</span>}
                  <div className="text-xs text-slate-500 mt-1">
                    Score: {current.audit_score_before} → {current.audit_score_after} (below 4 = flagged)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="mt-6 border-t pt-4 space-y-3">
            <div>
              <label className="text-xs uppercase text-slate-500">Optional notes for this decision</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., 'legitimate reuse — Priya coffee ceremony module'"
                className="mt-1"
                rows={2}
                data-testid="audit-notes"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">
                If regenerating: describe the correct scene (defaults to first sentence of stimulus)
              </label>
              <Textarea
                value={regenTopic}
                onChange={(e) => setRegenTopic(e.target.value)}
                placeholder={_defaultTopic(current.stimulus)}
                className="mt-1"
                rows={2}
                data-testid="audit-regen-topic"
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={() => submit('correct')}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="audit-mark-correct-btn"
              >
                <CheckCircle2 size={16} className="mr-2" /> Correct — keep image
              </Button>
              <Button
                onClick={() => submit('wrong_regen')}
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="audit-mark-regen-btn"
              >
                <RefreshCw size={16} className="mr-2" /> Wrong — regenerate
              </Button>
              <Button
                onClick={() => submit('wrong_reassign')}
                disabled={submitting}
                variant="outline"
                className="border-rose-400 text-rose-700 hover:bg-rose-50"
                data-testid="audit-mark-reassign-btn"
              >
                <ArrowRightLeft size={16} className="mr-2" /> Wrong — I'll reassign manually
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Recent regen jobs */}
      {queue.recent?.length > 0 && (
        <Card className="p-4">
          <div className="text-sm font-semibold text-slate-800 mb-2">Recent regen jobs</div>
          <div className="text-xs text-slate-600 space-y-1 max-h-64 overflow-y-auto">
            {queue.recent.slice(0, 30).map((j) => (
              <div key={j.job_id} className="flex items-center gap-2 py-1 border-b last:border-b-0">
                <Badge className={
                  j.status === 'done' ? 'bg-emerald-100 text-emerald-800' :
                  j.status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                  j.status === 'failed' ? 'bg-rose-100 text-rose-800' :
                  'bg-slate-100 text-slate-700'
                }>{j.status}</Badge>
                <span className="font-mono">{j.question_id.slice(0, 8)}</span>
                <span className="italic text-slate-700 flex-1 truncate">{j.image_topic}</span>
                {j.result_url && (
                  <a href={j.result_url} target="_blank" rel="noreferrer" className="text-blue-600">preview</a>
                )}
                {j.error && <span className="text-rose-700 text-xs truncate max-w-xs" title={j.error}>{j.error}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

function _defaultTopic(stim) {
  if (!stim) return 'school scene with children';
  const first = stim.split(/[.!?]/)[0].trim();
  return first.slice(0, 140);
}

export default SemanticAuditReviewTab;
